import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  buildAppLauncherAppsUrl,
  MyHivveAppClient,
  MyHivveLoadAppsOptions,
  normalizeAppLauncherApps
} from '@hivvetech/myhivve-oidc-client';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '@env';

const PRODUCT_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  ImpactTracker: 'Impact Tracker',
  TrackImpact: 'Impact Showcase',
  myvvid: 'My Hivve'
};

export function getProductDisplayName(app: Pick<MyHivveAppClient, 'name'>): string {
  return PRODUCT_DISPLAY_NAMES[app.name] ?? app.name;
}

@Injectable({
  providedIn: 'root'
})
export class AppLauncherService {
  private readonly clientId = environment.awsCognitoConfig.cognito.Auth.userPoolWebClientId;

  constructor(private readonly http: HttpClient) {}

  loadApps(options: MyHivveLoadAppsOptions = {}): Observable<MyHivveDashboardAppClient[]> {
    const url = buildAppLauncherAppsUrl(
      {
        authority: window.location.origin,
        clientId: this.clientId
      },
      {
        primaryApp: true,
        ...options
      }
    );

    return this.http.get<unknown>(url).pipe(
      map(response => this.toDashboardApps(response)),
      catchError(error => {
        console.warn('App launcher load failed:', error);
        return of([]);
      })
    );
  }

  private toDashboardApps(response: unknown): MyHivveDashboardAppClient[] {
    const apps = normalizeAppLauncherApps(response);

    // normalizeAppLauncherApps only accepts a top-level array, so any other shape yields no apps
    // and there is nothing to enrich with access state.
    if (!Array.isArray(response)) {
      return apps;
    }

    const accessStateByClientId = this.mapAccessStateSourcesByClientId(response);

    return apps.map(app => ({
      ...app,
      ...this.readAppAccessState(accessStateByClientId.get(app.clientId))
    }));
  }

  private mapAccessStateSourcesByClientId(items: unknown[]): Map<string, Record<string, unknown>> {
    const sourcesByClientId = new Map<string, Record<string, unknown>>();

    items.forEach(item => {
      if (!this.isRecord(item)) {
        return;
      }

      const clientId =
        this.readOptionalString(item, 'clientId') ?? this.readOptionalString(item, 'client_id');

      if (clientId) {
        sourcesByClientId.set(clientId, item);
      }
    });

    return sourcesByClientId;
  }

  private readAppAccessState(source?: Record<string, unknown>): MyHivveAppAccessState {
    if (!source) {
      return {};
    }

    return {
      accessState:
        this.readOptionalString(source, 'accessState') ??
        this.readOptionalString(source, 'access_state'),
      canLaunch:
        this.readOptionalBoolean(source, 'canLaunch') ??
        this.readOptionalBoolean(source, 'can_launch'),
      isLocked:
        this.readOptionalBoolean(source, 'isLocked') ??
        this.readOptionalBoolean(source, 'is_locked'),
      launchable: this.readOptionalBoolean(source, 'launchable'),
      lockReason:
        this.readOptionalString(source, 'lockReason') ??
        this.readOptionalString(source, 'lock_reason')
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private readOptionalBoolean(source: Record<string, unknown>, key: string): boolean | undefined {
    const value = source[key];

    return typeof value === 'boolean' ? value : undefined;
  }

  private readOptionalString(source: Record<string, unknown>, key: string): string | undefined {
    const value = source[key];

    return typeof value === 'string' && value.trim() ? value : undefined;
  }
}

export const APP_ACCESS_STATE = {
  AVAILABLE: 'available',
  LOCKED: 'locked',
  UNAVAILABLE: 'unavailable'
} as const;

export interface MyHivveDashboardAppClient extends MyHivveAppClient, MyHivveAppAccessState {}

export interface MyHivveAppAccessState {
  accessState?: string;
  canLaunch?: boolean;
  isLocked?: boolean;
  launchable?: boolean;
  lockReason?: string;
}
